/**
 * Backfill wetland ratings from the parsed NWPL data.
 * Requires wetlandRatings.json to be generated first via scripts/parse-nwpl.js
 */
import { sharedDb, recordChange } from '@monorepo/shared';
import db from './db';

let wetlandData = null;

/**
 * Load the wetland ratings JSON (generated from NWPL XLSX files).
 * Returns null if the file doesn't exist (hasn't been generated yet).
 */
async function loadWetlandData() {
  if (wetlandData) return wetlandData;
  try {
    const data = await import('../data/wetlandRatings.json');
    wetlandData = data.default || data;
    return wetlandData;
  } catch (err) {
    console.warn('wetlandRatings.json not found. Run scripts/parse-nwpl.js first.');
    return null;
  }
}

/**
 * Look up wetland ratings for a scientific name.
 * Returns { agcp: string|null, emp: string|null }
 */
export async function lookupWetlandRating(scientificName) {
  const data = await loadWetlandData();
  if (!data) return { agcp: null, emp: null };

  // Normalize to lowercase binomial
  const normalized = scientificName.trim().split(/\s+/).slice(0, 2).join(' ').toLowerCase();

  return {
    agcp: data.agcp?.[normalized] || null,
    emp: data.emp?.[normalized] || null,
  };
}

/**
 * Backfill wetland ratings for all species missing AGCP/EMP data.
 * Returns { updated: number, notFound: number, skipped: number }
 */
export async function backfillWetlandRatings() {
  const data = await loadWetlandData();
  if (!data) {
    return { updated: 0, notFound: 0, skipped: 0, error: 'wetlandRatings.json not found. Download NWPL XLSX files and run: node scripts/parse-nwpl.js' };
  }

  const allSpecies = await sharedDb.plantSpecies.toArray();
  let updated = 0;
  let notFound = 0;
  let skipped = 0;

  for (const species of allSpecies) {
    // Skip if already has both ratings
    if (species.agcpRating && species.empRating) {
      skipped++;
      continue;
    }

    const ratings = await lookupWetlandRating(species.scientificName);
    const updates = {};

    if (!species.agcpRating && ratings.agcp) updates.agcpRating = ratings.agcp;
    if (!species.empRating && ratings.emp) updates.empRating = ratings.emp;

    if (Object.keys(updates).length > 0) {
      const merged = { ...species, ...updates, updatedAt: new Date().toISOString() };
      await sharedDb.plantSpecies.put(merged);
      recordChange('plantSpecies', species.id, 'put');

      // Also update all plant instances with this species
      const allInstances = await db.plants.toArray();
      const instances = allInstances.filter(p => p.commonName === species.commonName && p.scientificName === species.scientificName);
      for (const inst of instances) {
        const instUpdates = {};
        if (!inst.agcpRating && updates.agcpRating) instUpdates.agcpRating = updates.agcpRating;
        if (!inst.empRating && updates.empRating) instUpdates.empRating = updates.empRating;
        if (Object.keys(instUpdates).length > 0) {
          await db.plants.put({ ...inst, ...instUpdates, updatedAt: new Date().toISOString() });
          recordChange('plants', inst.id, 'put');
        }
      }

      updated++;
    } else if (!ratings.agcp && !ratings.emp) {
      notFound++;
    } else {
      skipped++;
    }
  }

  return { updated, notFound, skipped };
}
